import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CandidateHrVoiceScreeningModal from '../../components/CandidateHrVoiceScreeningModal';

const HrScreeningQuestionnaire = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unlocked, setUnlocked] = useState(false);
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showVoiceModal, setShowVoiceModal] = useState(false);

  const [timeLeftStr, setTimeLeftStr] = useState('');
  const [isExpired, setIsExpired] = useState(false);

  const API_BASE = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || '/erp/backend/api';

  useEffect(() => {
    fetchQuestionnaire();
  }, [id]);

  useEffect(() => {
    if (!data?.hr_screening_deadline || result) return;

    const calculateTime = () => {
      const deadline = new Date(data.hr_screening_deadline).getTime();
      const now = new Date().getTime();
      const diff = deadline - now;

      if (diff <= 0 || data.is_expired || data.hr_screening_status === 'EXPIRED') {
        setTimeLeftStr('00h 00m 00s');
        setIsExpired(true);
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeLeftStr(`${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`);
    };

    calculateTime();
    const interval = setInterval(calculateTime, 1000);
    return () => clearInterval(interval);
  }, [data, result]);

  const fetchQuestionnaire = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE}/job-applications/${id}/hr-screening-questionnaire`);
      const json = await res.json();

      if (!json.success) {
        setUnlocked(false);
        setError(json.message || json.error || 'Failed to load questionnaire');
        return;
      }

      setData(json.data);
      setUnlocked(json.data.unlocked !== false);
      if (json.data.is_expired || json.data.hr_screening_status === 'EXPIRED') {
        setIsExpired(true);
      }
      if (json.data.saved_answers?.answers) {
        setAnswers(json.data.saved_answers.answers);
      }
      if (json.data.hr_screening_score !== null && json.data.hr_screening_score !== undefined) {
        setResult({
          requirement_match_score: json.data.hr_screening_score,
          hr_screening_status: json.data.hr_screening_status,
          is_passed: json.data.hr_screening_score >= 70,
          evaluation: json.data.saved_answers?.requirements_eval || json.data.saved_answers?.evaluation || []
        });
      }
    } catch (err) {
      setError(err.message || 'Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  const formatCriterionLabel = (label) => {
    if (!label) return 'Requirement Verification';
    if (label === 'q_notice' || label === 'req_notice') return 'Notice Period & Availability';
    if (label === 'q_shift' || label === 'req_shift') return 'Work Shift & Schedule Compatibility';
    return label.replace(/^q_/, '').replace(/^req_/, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleInputChange = (qId, value) => {
    setAnswers(prev => ({
      ...prev,
      [qId]: value
    }));
  };

  const handleSkillToggle = (skill) => {
    setAnswers(prev => {
      const current = Array.isArray(prev.req_skills) ? prev.req_skills : [];
      const updated = current.includes(skill)
        ? current.filter(s => s !== skill)
        : [...current, skill];
      return { ...prev, req_skills: updated };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError(null);

      const res = await fetch(`${API_BASE}/job-applications/${id}/submit-hr-screening`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers })
      });
      const json = await res.json();

      if (!json.success) {
        if (res.status === 410 || json.data?.is_expired) {
          setIsExpired(true);
        }
        throw new Error(json.message || 'Submission failed');
      }

      setResult(json.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-4">
        <div className="flex items-center gap-3 text-emerald-400">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-400"></div>
          <span className="font-medium text-sm">Loading HR Screening Questionnaire...</span>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-2xl p-6 text-center shadow-xl">
          <div className="w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto mb-4 text-amber-400">
            <Icon name="Lock" size={24} />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Stage Locked</h2>
          <p className="text-slate-400 text-sm mb-6">{error}</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 py-10 px-4 sm:px-6">
      <div className="max-w-2xl mx-auto space-y-6">

        {/* Top Navigation */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <button
            onClick={() => navigate('/job-marketplace')}
            className="inline-flex items-center gap-2 text-xs font-semibold text-slate-400 hover:text-white transition-colors bg-slate-900 border border-slate-800 px-3.5 py-2 rounded-xl"
          >
            <Icon name="ArrowLeft" size={16} />
            <span>Back to Job Marketplace</span>
          </button>

          {!result && !isExpired && (
            <button
              onClick={() => setShowVoiceModal(true)}
              className="inline-flex items-center gap-2 text-xs font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 border border-blue-500/30 px-4 py-2 rounded-xl shadow-lg shadow-blue-500/20 transition-all transform hover:scale-105"
            >
              <Icon name="Mic" size={16} />
              <span>Launch Voice HR Screening (Recommended)</span>
            </button>
          )}
        </div>

        {/* Header Branding */}
        <div className="bg-slate-900/80 border border-slate-800 backdrop-blur-md rounded-2xl p-6 shadow-xl">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 mb-2">
                <Icon name="CheckCircle2" size={14} /> Candidate Portal
              </span>
              <h1 className="text-2xl font-bold text-white">{data?.position || 'Job Application'}</h1>
              <p className="text-slate-400 text-sm">{data?.company} &bull; Candidate: <span className="text-slate-200 font-medium">{data?.applicant_name}</span></p>
            </div>
            <div className="text-right">
              <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold block">Stage</span>
              <span className="text-sm font-semibold text-emerald-400 block">HR Screening Questionnaire</span>
              {data?.hr_screening_deadline && !result && (
                <div className={`mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${
                  isExpired ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-blue-500/20 text-blue-300 border border-blue-500/30 animate-pulse'
                }`}>
                  <Icon name="Clock" size={13} />
                  <span>{isExpired ? 'Deadline Expired' : `${timeLeftStr} remaining`}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Submission Confirmation Receipt */}
        {result ? (
          <div className="bg-slate-900 border border-emerald-500/30 rounded-2xl p-6 shadow-2xl space-y-6 animate-fade-in">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white text-2xl font-bold ${
                result.is_passed ? 'bg-emerald-600' : 'bg-amber-600'
              }`}>
                {Math.round(result.requirement_match_score)}%
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">
                  {result.is_passed ? 'Requirement Matching Passed!' : 'Questionnaire Submitted'}
                </h3>
                <p className="text-slate-400 text-sm">
                  {result.is_passed
                    ? 'Your responses match key requirements! Your application has been automatically advanced to the next interview stage.'
                    : 'Thank you! Your responses have been recorded and sent to HR for manual review.'}
                </p>
              </div>
            </div>

            {result.evaluation && result.evaluation.length > 0 && (
              <div className="border-t border-slate-800 pt-4 space-y-3">
                <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Requirement Evaluation Summary</h4>
                <div className="space-y-2">
                  {result.evaluation.map((item, idx) => {
                    const label = formatCriterionLabel(item.criterion || item.requirement || item.id);
                    const requiredText = item.required || item.requirement || item.criterion || 'Specified Role Requirement';
                    const submittedText = item.submitted || item.answer || 'Response recorded';

                    return (
                      <div key={idx} className="flex items-center justify-between bg-slate-950 p-3 rounded-lg border border-slate-800 text-xs">
                        <div>
                          <span className="font-semibold text-slate-200">{label}</span>
                          <p className="text-slate-400 mt-0.5">
                            <span className="font-medium text-slate-300">Required:</span> {requiredText} &bull; <span className="font-medium text-slate-300">Submitted:</span> {submittedText}
                          </p>
                        </div>
                        <span className={`px-2.5 py-1 rounded font-semibold text-[11px] flex-shrink-0 ${
                          item.passed ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                        }`}>
                          {item.score}% Match
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="pt-2 text-center">
              <p className="text-xs text-slate-500">You may close this tab or return to the main portal.</p>
            </div>
          </div>
        ) : isExpired || data?.is_expired || data?.hr_screening_status === 'EXPIRED' ? (
          <div className="bg-slate-900 border border-amber-500/40 rounded-2xl p-6 shadow-2xl space-y-4 animate-fade-in">
            <div className="flex items-center gap-3 text-amber-400">
              <div className="w-12 h-12 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center font-bold">
                <Icon name="AlertTriangle" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">HR Screening Questionnaire Deadline Expired</h3>
                <p className="text-xs text-slate-400">24-Hour Candidate Window Missed</p>
              </div>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">
              The 24-hour window to complete your HR Screening Questionnaire has expired. Submissions are no longer accepted for this stage.
            </p>
            <p className="text-xs text-slate-400 italic border-t border-slate-800 pt-3">
              If you believe this is an error or require a deadline extension, please contact the HR recruitment team directly.
            </p>
          </div>
        ) : (
          /* Questionnaire Form */
          <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl space-y-6">
            <div className="border-b border-slate-800 pb-4">
              <h2 className="text-lg font-bold text-white">Requirement Verification Questionnaire</h2>
              <p className="text-slate-400 text-xs mt-1">
                Please answer the following screening questions to verify your fit against the role requirements.
              </p>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-xl text-xs">
                {error}
              </div>
            )}

            {/* Questions list */}
            {data?.questions?.map((q, idx) => {
              const rawType = (q.type || 'textarea').toLowerCase();
              const isKnownSpecial = ['experience', 'location', 'skills', 'yes_no', 'short_text', 'input'].includes(rawType);

              return (
                <div key={q.id || idx} className="space-y-2 bg-slate-950/60 border border-slate-800/80 p-4 rounded-xl">
                  <label className="block text-sm font-medium text-slate-200">
                    <span className="text-emerald-400 font-semibold mr-1.5">{idx + 1}.</span>
                    {q.question}
                  </label>

                  {/* Experience Question */}
                  {rawType === 'experience' && (
                    <div className="flex items-center gap-3 pt-1">
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        max="30"
                        value={answers[q.id] || ''}
                        onChange={(e) => handleInputChange(q.id, e.target.value)}
                        placeholder="e.g. 3.5"
                        required
                        className="w-36 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500"
                      />
                      <span className="text-slate-400 text-xs">Years of experience</span>
                    </div>
                  )}

                  {/* Location Question */}
                  {rawType === 'location' && (
                    <div className="space-y-2 pt-1">
                      {['Yes, currently based in this location', 'Yes, willing to relocate', 'No, remote work only'].map((opt, oIdx) => (
                        <label key={oIdx} className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                          <input
                            type="radio"
                            name={q.id}
                            value={opt}
                            checked={answers[q.id] === opt}
                            onChange={(e) => handleInputChange(q.id, e.target.value)}
                            className="text-emerald-500 focus:ring-emerald-500"
                            required
                          />
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {/* Skills Question */}
                  {rawType === 'skills' && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1">
                      {q.options?.map((skill, sIdx) => {
                        const isChecked = Array.isArray(answers[q.id]) && answers[q.id].includes(skill);
                        return (
                          <button
                            type="button"
                            key={sIdx}
                            onClick={() => handleSkillToggle(skill)}
                            className={`flex items-center justify-between p-2.5 rounded-lg border text-xs text-left transition-all ${
                              isChecked
                                ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300 font-semibold'
                                : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700'
                            }`}
                          >
                            <span>{skill}</span>
                            {isChecked && <Icon name="Check" size={14} className="text-emerald-400" />}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Yes/No Question */}
                  {rawType === 'yes_no' && (
                    <div className="flex items-center gap-4 pt-1">
                      {['Yes', 'No'].map((yn, ynIdx) => (
                        <label key={ynIdx} className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                          <input
                            type="radio"
                            name={q.id}
                            value={yn}
                            checked={answers[q.id] === yn}
                            onChange={(e) => handleInputChange(q.id, e.target.value)}
                            className="text-emerald-500"
                            required
                          />
                          <span>{yn}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {/* Single-line input */}
                  {(rawType === 'short_text' || rawType === 'input') && (
                    <div className="pt-1">
                      <input
                        type="text"
                        value={answers[q.id] || ''}
                        onChange={(e) => handleInputChange(q.id, e.target.value)}
                        placeholder="Type your response..."
                        required
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                  )}

                  {/* Multi-line Textarea (text, textarea, long_text, paragraph, custom, or missing/unrecognized type) */}
                  {!isKnownSpecial && (
                    <div className="pt-1">
                      <textarea
                        rows={4}
                        value={answers[q.id] || ''}
                        onChange={(e) => handleInputChange(q.id, e.target.value)}
                        placeholder="Describe your experience and response in detail..."
                        required
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-sm text-white focus:outline-none focus:border-emerald-500 min-h-[105px] resize-y"
                      />
                    </div>
                  )}
                </div>
              );
            })}

            <div className="pt-4 border-t border-slate-800 flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-sm px-6 py-2.5 rounded-xl flex items-center gap-2 shadow-lg shadow-emerald-900/30 disabled:opacity-50 transition-all"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Evaluating Answers...</span>
                  </>
                ) : (
                  <>
                    <Icon name="Send" size={16} />
                    <span>Submit HR Questionnaire</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>

      {showVoiceModal && (
        <CandidateHrVoiceScreeningModal
          isOpen={showVoiceModal}
          applicationId={id}
          initialData={data}
          onClose={() => setShowVoiceModal(false)}
          onCompleted={(resData) => {
            setShowVoiceModal(false);
            if (resData) {
              setResult({
                requirement_match_score: resData.requirement_match_score,
                hr_screening_status: resData.hr_screening_status,
                is_passed: resData.is_passed,
                evaluation: resData.evaluation || []
              });
            }
          }}
        />
      )}
    </div>
  );
};

export default HrScreeningQuestionnaire;
